import {useState, useEffect, useCallback} from 'react'
import {useRouteMatch} from 'react-router'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {MRT_API_BASEPATH} from '../ssr/constants'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useLocaleCountry} from '../hooks/use-locale-custom'
import {useTokenHelper} from '../utils/token-helper'

const useOrderDetails = (guest = false) => {
    const appOrigin = useOrigin(false)
    const {data: customer, isFetched: isCustomerFetched} = useCurrentCustomer()
    const [isLoading, setIsLoading] = useState(false)
    const [order, setOrder] = useState(true)
    const {params} = useRouteMatch()
    const country = useLocaleCountry()
    const {fetchOMSValidToken} = useTokenHelper()
    const {orderId, orderNo, originalOrderId} = params

    const fetchOrderDetails = useCallback(async () => {
        try {
            setIsLoading(true)
            const accessToken = await fetchOMSValidToken()
            const queryParams = new URLSearchParams({
                orderSummaryId: orderId,
                gigyaId: customer?.c_gigyaId,
                orderNumber: orderNo,
                originalOrderId: originalOrderId,
                country: country,
                email: customer?.email,
                guest: guest,
            })

            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/get-order-details-oms?${queryParams.toString()}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                },
            )
            const result = await response.json()
            if (result.success) {
                setOrder({
                    ...result.order,
                })
            }
        } catch (error) {
            // TODO:
            console.error('Error fetching order details:', error)
        } finally {
            setIsLoading(false)
        }
    }, [orderId, orderNo, originalOrderId, customer?.c_gigyaId])

    useEffect(() => {
        if (!guest && !isCustomerFetched) {
            return
        }
        if (!orderId && (!customer?.c_gigyaId || !orderNo)) return
        fetchOrderDetails()
    }, [isCustomerFetched, orderId, customer?.c_gigyaId, orderNo, fetchOrderDetails])

    const refetchOrder = useCallback(() => {
        fetchOrderDetails()
    }, [fetchOrderDetails])

    return {order, isLoading, refetchOrder}
}

/**
 * Hook per ottenere i dettagli di un ordine basandosi su un oggetto `order` passato come argomento.
 * @param {Object} order - Oggetto ordine contenente i parametri necessari.
 * @returns {Object} - Dettagli dell'ordine, stato di caricamento e eventuali errori.
 */
const useOrderDetailsFromOrder = (order, guest = false) => {
    const [orderDetails, setOrderDetails] = useState(null)
    const {data: customer} = useCurrentCustomer()
    const country = useLocaleCountry()
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)
    const appOrigin = useOrigin(false)
    const {fetchOMSValidToken} = useTokenHelper()

    useEffect(() => {
        const fetchOrderDetails = async () => {
            if (!order?.Id || !order?.OrderNumber || !order?.OriginalOrderId) {
                setError('Missing required order parameters')
                setIsLoading(false)
                return
            }

            try {
                setIsLoading(true)

                const accessToken = await fetchOMSValidToken()

                const queryParams = new URLSearchParams({
                    orderSummaryId: order.Id,
                    orderNumber: order.OrderNumber,
                    originalOrderId: order.OriginalOrderId,
                    gigyaId: customer?.c_gigyaId,
                    country: country,
                    email: customer?.email,
                    guest: guest,
                })

                const response = await fetch(
                    `${appOrigin}${MRT_API_BASEPATH}/get-order-details-oms?${queryParams.toString()}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${accessToken}`,
                        },
                    },
                )

                const result = await response.json()
                if (result.success) {
                    setOrderDetails(result.order)
                } else {
                    setError('Failed to fetch order details: ' + result.error)
                }
            } catch (err) {
                console.error('Error fetching order details:', err)
                setError('Error fetching order details: ' + err.message)
            } finally {
                setIsLoading(false)
            }
        }

        if (order) {
            fetchOrderDetails()
        }
    }, [order, appOrigin])

    return {orderDetails, isLoading, error}
}

export {useOrderDetails, useOrderDetailsFromOrder}
